import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { DataGrid, GridRowModes, GridActionsCellItem } from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ROUTES } from "../../constants";
import DataGridInfoAlert from "../../components/dataAdminGrid/InfoAlert";
import AllocationProfilesToolbar from "./AllocationProfilesToolbar";
import DeleteDialog from "../../components/dataAdminGrid/DeleteDialog";
import AllocationProfileDialog from "./AllocationProfileDialog";
import AllocationProfileViewDialog from "./AllocationProfileViewDialog";
import { useApp } from "../../AppProvider";

const API_BASE_URL = `${process.env.REACT_APP_ENDPOINT}/api/allocation-profiles`;

const StyledDataGrid = styled(DataGrid)({
  height: 600,
  "& .MuiDataGrid-row:hover": {
    cursor: "pointer",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "#d9e7f5",
  },
  "& .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "#c3dbf2",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing": {
    backgroundColor: "#fff8dc",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing .MuiDataGrid-cell": {
    backgroundColor: "#fff8dc",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing:hover .MuiDataGrid-cell": {
    backgroundColor: "#ffeeba",
  },
});

function AllocationProfilesPage() {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { doToast } = useApp();
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [viewProfile, setViewProfile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);
  const [rowModesModel, setRowModesModel] = useState({});

  // Fetch all profiles (including inactive) and filter client-side
  const { data: allProfiles = [], isLoading } = useQuery(
    ["allocation-profiles"],
    async () => {
      const token = await getAccessTokenSilently();
      const res = await axios.get(API_BASE_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.map((profile) => ({
        ...profile,
        id: profile.profile_ndx, // Add id field for DataGrid
      }));
    }
  );

  // Filter profiles based on showInactive state
  const profiles = useMemo(() => {
    if (showInactive) {
      return allProfiles; // Show all profiles
    }
    return allProfiles.filter((profile) => profile.is_active); // Show only active profiles
  }, [allProfiles, showInactive]);

  // Delete (deactivate) mutation
  const deleteMutation = useMutation(
    async (profileNdx) => {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_BASE_URL}/${profileNdx}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    {
      onSuccess: () => {
        void queryClient.invalidateQueries("allocation-profiles");
        doToast("success", "Profile deactivated successfully");
      },
      onError: () => {
        doToast("error", "Failed to deactivate profile");
      },
    }
  );

  const handleViewClick = useCallback(
    (id) => () => {
      const profile = profiles.find((p) => p.profile_ndx === id);
      setViewProfile(profile);
      setViewDialogOpen(true);
    },
    [profiles]
  );

  const handleEditClick = useCallback(
    (id) => () => {
      const profile = profiles.find((p) => p.profile_ndx === id);
      setSelectedProfile(profile);
      setDialogOpen(true);
    },
    [profiles]
  );

  const handleDeleteClick = useCallback(
    (id) => () => {
      const profile = profiles.find((p) => p.profile_ndx === id);
      setProfileToDelete(profile);
      setDeleteDialogOpen(true);
    },
    [profiles]
  );

  const handleConfirmDelete = async () => {
    if (profileToDelete) {
      await deleteMutation.mutateAsync(profileToDelete.profile_ndx);
      setDeleteDialogOpen(false);
      setProfileToDelete(null);
    }
  };

  const handleAddNewRecord = () => {
    setSelectedProfile(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedProfile(null);
  };

  const handleDialogSave = () => {
    void queryClient.invalidateQueries("allocation-profiles");
    handleDialogClose();
  };

  const columns = useMemo(
    () => [
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 120,
        cellClassName: "actions",
        getActions: ({ id, row }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                icon={<SaveIcon />}
                label="Save"
                onClick={() =>
                  setRowModesModel((prev) => ({
                    ...prev,
                    [id]: { mode: GridRowModes.View },
                  }))
                }
              />,
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={() =>
                  setRowModesModel((prev) => ({
                    ...prev,
                    [id]: {
                      mode: GridRowModes.View,
                      ignoreModifications: true,
                    },
                  }))
                }
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              icon={<VisibilityIcon />}
              label="View"
              className="textPrimary"
              onClick={handleViewClick(row.profile_ndx)}
              color="inherit"
            />,
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(row.profile_ndx)}
              color="inherit"
            />,
            row.is_active && (
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={handleDeleteClick(row.profile_ndx)}
                color="inherit"
              />
            ),
          ].filter(Boolean);
        },
      },
      {
        field: "profile_ndx",
        headerName: "ID",
        width: 80,
        editable: false,
      },
      {
        field: "profile_name",
        headerName: "Profile Name",
        flex: 1,
        minWidth: 200,
        editable: true,
      },
      {
        field: "is_active",
        headerName: "Active",
        width: 100,
        type: "boolean",
        editable: false,
        renderCell: (params) => (
          <Typography color={params.value ? "success.main" : "error.main"}>
            {params.value ? "Active" : "Inactive"}
          </Typography>
        ),
      },
      {
        field: "total_percent",
        headerName: "Total %",
        width: 120,
        editable: false,
        valueGetter: (params) => {
          const items = params?.row?.items || [];
          const total = items.reduce(
            (sum, item) => sum + parseFloat(item.percent_of_year || 0),
            0
          );
          return (total * 100).toFixed(2) + "%";
        },
      },
    ],
    [rowModesModel, handleViewClick, handleEditClick, handleDeleteClick]
  );

  return (
    <Box>
      <Helmet title="Allocation Profiles Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Allocation Profiles Management
      </Typography>

      <Breadcrumbs sx={{ mt: 4 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <MuiLink component={RouterLink} to={ROUTES.PERMITS_MANAGEMENT}>
          Permits
        </MuiLink>
        <Typography>Allocation Profiles</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      <DataGridInfoAlert
        title="Manage allocation profiles:"
        description="Create and manage monthly allocation profiles for permits. Each profile defines how annual allocations are distributed across 12 months."
      />

      <StyledDataGrid
        rows={profiles}
        columns={columns}
        loading={isLoading}
        slots={{
          toolbar: AllocationProfilesToolbar,
        }}
        slotProps={{
          toolbar: {
            onAddNewRecord: handleAddNewRecord,
            showInactive: showInactive,
            onShowInactiveChange: setShowInactive,
          },
        }}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        disableRowSelectionOnClick
        onCellDoubleClick={(params) => {
          // Disable double-click editing and instead open view dialog
          const profile = profiles.find(
            (p) => p.profile_ndx === params.row.profile_ndx
          );
          setViewProfile(profile);
          setViewDialogOpen(true);
        }}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        message={`Are you sure you want to deactivate "${profileToDelete?.profile_name}"? This will make it unavailable for new permits.`}
      />

      <AllocationProfileViewDialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        profile={viewProfile}
      />

      <AllocationProfileDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        profile={selectedProfile}
        apiBaseUrl={API_BASE_URL}
      />
    </Box>
  );
}

export default AllocationProfilesPage;
